/**
 * Derives left/right panel state from messages + loadingMessage (same shape as
 * transformChatHistory and processSSEMessage). Used when AgenticTwoPanelLayout
 * receives messages/loadingMessage instead of explicit conversationMessages,
 * phaseCards, etc.
 *
 * New format: Each AgenticChatMessage represents a question-response pair.
 * - user_input: question (left panel user bubble)
 * - summarized_title: consolidated question label for right panel parent accordion title (overall question only)
 * - content: summary (right panel, markdown)
 * - additional_kwargs.intermediate_steps: Refining/Understanding/History Use (starting text), Planning (grey box), Executing (phase cards)
 * - additional_kwargs.task_complete_events: Task completions with visualizations, key_takeaways, formulae (primary source for right panel when present)
 * - additional_kwargs.overview: One-liner shown above followup questions (left panel bottom textual response)
 * - additional_kwargs.key_takeaways: Key findings for right panel Analysis Summary card
 */

import React from 'react';
import type { TableSample } from '../../../molecules/ViewMethodology/DataSourceTables';
import type {
    AgenticChatMessage,
    AgenticIntermediateStep,
    AgenticConversationMessage,
    AgenticConversationPair,
    AgenticRunningTask,
    AgenticPlannerTasks,
    AgenticPhaseCard,
    AgenticAccordionItem,
    AgenticChildAccordionItem,
    ChartVisualizationContent,
    ScenarioLink,
    AnalysisSummaryContent,
    TakeawayItem,
    TakeawayTextSegment,
    AgenticTakeawaySource,
    SuggestedAction,
    AgenticPlanningTask,
    RightPanelBlock,
    AgenticAnalysisSummary,
    DeriveLayoutFeedbackHandlers,
    RightPanelFeedbackPayload,
    RightPanelDislikeFeedbackPayload,
    DislikeFeedbackSubmitPayload,
    AgenticEntityDisambiguation,
    AgenticEntityDisambiguationMatch,
    AgenticEntityDisambiguationTerm,
    AgenticOutOfScopeFeedback,
} from '../AskAsperAgentic.types';
import { isVizDataErrorState } from '../../../../utils/transformVizData';
import type { AgenticFeedbackHistoryResponse } from '../AskAsperAgentic.types';
import { getInitialVoteForSummary, getInitialVoteForTask } from '../utils/feedbackHistory';
import { stripLookupTokenMarkers } from '../utils/lookupTokenMarkers';
import { pickHtmlChartFields, hasHtmlChartDocument, type AgenticHtmlChartFields } from '../../../shared/EChartsHtmlChart';

function buildAccordionFeedbackHandlers(
    handlers: DeriveLayoutFeedbackHandlers | undefined,
    ctx: RightPanelFeedbackPayload,
    dislikeContextTitle: string,
    initialFeedbackVote?: AgenticChildAccordionItem['initialFeedbackVote'],
): Partial<
    Pick<
        AgenticChildAccordionItem,
        | 'onThumbsUp'
        | 'onThumbsDown'
        | 'onDislikeFeedbackSubmit'
        | 'dislikeFeedbackContextTitle'
        | 'initialFeedbackVote'
    >
> {
    const out: Partial<
        Pick<
            AgenticChildAccordionItem,
            | 'onThumbsUp'
            | 'onThumbsDown'
            | 'onDislikeFeedbackSubmit'
            | 'dislikeFeedbackContextTitle'
            | 'initialFeedbackVote'
        >
    > = {
        dislikeFeedbackContextTitle: dislikeContextTitle,
        ...(initialFeedbackVote !== undefined ? { initialFeedbackVote } : {}),
    };
    if (!handlers) return out;
    const { onRightPanelThumbsUp, onRightPanelThumbsDown, onRightPanelDislikeFeedbackSubmit } = handlers;
    if (onRightPanelThumbsUp) {
        out.onThumbsUp = () => onRightPanelThumbsUp(ctx);
    }
    if (onRightPanelThumbsDown) {
        out.onThumbsDown = () => onRightPanelThumbsDown(ctx);
    }
    if (onRightPanelDislikeFeedbackSubmit) {
        out.onDislikeFeedbackSubmit = (payload: DislikeFeedbackSubmitPayload) =>
            onRightPanelDislikeFeedbackSubmit({ ...ctx, ...payload } as RightPanelDislikeFeedbackPayload);
    }
    return out;
}

function getIntermediateSteps(msg: AgenticChatMessage | null | undefined): AgenticIntermediateStep[] {
    if (!msg) return [];
    const fromAnswer = (msg as any).answer?.data?.intermediate_steps;
    if (fromAnswer && Array.isArray(fromAnswer)) return fromAnswer;
    const fromKwargs = msg.additional_kwargs?.intermediate_steps;
    if (fromKwargs && Array.isArray(fromKwargs)) return fromKwargs;
    return [];
}

/** Consolidated question label from backend (post-clarification), when present. */
function getSummarizedTitleFromMessage(msg: AgenticChatMessage): string | undefined {
    const fromTop = typeof msg.summarized_title === 'string' ? msg.summarized_title.trim() : '';
    if (fromTop) return fromTop;
    const fromKwargs = msg.additional_kwargs?.summarized_title;
    if (typeof fromKwargs === 'string' && fromKwargs.trim() !== '') return fromKwargs.trim();
    return undefined;
}

/** Right panel question header — prefers backend consolidated title after clarification.
 *  Inline `/*entity*\/` lookup markers (from the slash-command flow) are stripped so the
 *  accordion title reads as natural language without the marker chars. */
function getRightPanelQuestionLabel(msg: AgenticChatMessage, fallbackQuestion: string, msgIdx: number): string {
    const raw = getSummarizedTitleFromMessage(msg) ?? (fallbackQuestion || `Response ${msgIdx + 1}`);
    return stripLookupTokenMarkers(raw);
}

function getContentFromMessage(msg: AgenticChatMessage): string {
    const raw = msg as any;
    if (raw.answer?.data?.output) {
        const out = raw.answer.data.output;
        return typeof out === 'string' ? out : (out?.text ?? String(out ?? ''));
    }
    return msg.content ?? '';
}

function isEntityMatch(value: unknown): value is AgenticEntityDisambiguationMatch {
    if (typeof value !== 'object' || value == null) return false;
    const v = value as Record<string, unknown>;
    return typeof v.column_name === 'string' && typeof v.entity_name === 'string';
}

/**
 * Build entity-disambiguation card state from interrupt kwargs (populated by the host).
 *
 * `needs_clarification` are the ambiguous terms awaiting a pick. `selected_entities` are entities
 * from the same question the backend already matched — they become collapsed, pre-selected rows
 * (never a signal that the user answered).
 *
 * `isLatestMessage` locks the card for historical turns: a clarification with turns after it was
 * already answered, even when the host never stamped `clarification_resolved`.
 */
function getEntityDisambiguationFromMessage(
    msg: AgenticChatMessage,
    isLatestMessage: boolean,
): AgenticEntityDisambiguation | undefined {
    const kwargs = msg.additional_kwargs as
        | {
              interrupt?: boolean;
              needs_clarification?: unknown;
              selected_entities?: unknown;
              clarification_resolved?: boolean;
              /** Host-stamped picks, keyed by the ambiguous term they answer (set on Continue). */
              clarification_selections?: unknown;
              /** Host-stamped entity_names the user chose to ignore (set on Continue). */
              clarification_ignored?: unknown;
          }
        | undefined;
    if (!kwargs?.interrupt) return undefined;

    const needsRaw = kwargs.needs_clarification;
    if (!Array.isArray(needsRaw) || needsRaw.length === 0) return undefined;

    const ambiguousTerms: AgenticEntityDisambiguationTerm[] = [];
    for (const term of needsRaw) {
        if (typeof term !== 'object' || term == null) continue;
        const t = term as Record<string, unknown>;
        if (typeof t.entity_name !== 'string' || !t.entity_name.trim()) continue;
        const closeRaw = Array.isArray(t.close_matches) ? t.close_matches : [];
        const close_matches = closeRaw.filter(isEntityMatch);
        ambiguousTerms.push({ entity_name: t.entity_name, close_matches });
    }
    if (ambiguousTerms.length === 0) return undefined;

    const selectedRaw = Array.isArray(kwargs.selected_entities) ? kwargs.selected_entities : [];
    const selected = selectedRaw.filter(isEntityMatch);
    const initialSelections: Record<string, AgenticEntityDisambiguationMatch> = {};
    const ambiguousNames = new Set(ambiguousTerms.map((t) => t.entity_name));

    // Picks the host recorded on Continue — keyed by the ambiguous term they answer, so a resolved
    // card (live or from history) shows what the user chose.
    const pickedKeys = new Set<string>();
    const pickedRaw = kwargs.clarification_selections;
    if (typeof pickedRaw === 'object' && pickedRaw != null && !Array.isArray(pickedRaw)) {
        for (const [termName, match] of Object.entries(pickedRaw as Record<string, unknown>)) {
            if (!ambiguousNames.has(termName) || !isEntityMatch(match)) continue;
            initialSelections[termName] = match;
            pickedKeys.add(`${match.column_name}::${match.entity_name}`);
        }
    }

    // Terms the user ignored: they carry no selection, and a matched entity that was ignored must
    // not come back as a confirmed row.
    const ignoredRaw = kwargs.clarification_ignored;
    const ignoredTerms = Array.isArray(ignoredRaw)
        ? ignoredRaw.filter((n): n is string => typeof n === 'string' && n.trim().length > 0)
        : [];
    const ignoredSet = new Set(ignoredTerms);

    const confirmedTerms: AgenticEntityDisambiguationTerm[] = [];
    for (const match of selected) {
        if (ignoredSet.has(match.entity_name)) continue;
        // Skip an entity still being asked about, and skip the user's own picks — the host echoes
        // them back in selected_entities, but they belong to their ambiguous row, not a new one.
        if (ambiguousNames.has(match.entity_name)) continue;
        if (pickedKeys.has(`${match.column_name}::${match.entity_name}`)) continue;
        if (initialSelections[match.entity_name] != null) continue;
        confirmedTerms.push({
            entity_name: match.entity_name,
            close_matches: [match],
            confirmed: true,
        });
        initialSelections[match.entity_name] = match;
    }

    // An ignored backend match is dropped from `selected_entities` on resume, so re-create its row
    // (selection-free) from the ignored list — otherwise the row would vanish from a resolved card.
    for (const name of ignoredTerms) {
        if (ambiguousNames.has(name)) continue;
        if (confirmedTerms.some((t) => t.entity_name === name)) continue;
        confirmedTerms.push({ entity_name: name, close_matches: [], confirmed: true });
    }

    const resolved = Boolean(kwargs.clarification_resolved) || !isLatestMessage;

    return {
        messageId: msg.id ?? msg.run_id,
        terms: [...confirmedTerms, ...ambiguousTerms],
        initialSelections: Object.keys(initialSelections).length > 0 ? initialSelections : undefined,
        ignoredTerms: ignoredTerms.length > 0 ? ignoredTerms : undefined,
        resolved,
    };
}

type ChartWithMeta = AgenticHtmlChartFields & {
    viz_data: any;
    task_id?: string;
    task_name?: string;
    key_takeaways?: RawKeyTakeaway[];
    /** Formulae - backend sends as string[] */
    formulae?: string | string[];
    /** Assumptions - backend sends as string[] */
    assumptions?: string | string[];
    /** SQL queries used to produce this task's result - from task_output.sql_queries (sibling of result/visualizations) */
    sql_queries?: string[];
    table_samples?: TableSample[];
    /** Scenario link URL - from task_complete visualization */
    scenario_link?: string;
    /** Scenario links (one per created scenario) - from task_complete visualization, takes precedence over scenario_link */
    scenario_links?: ScenarioLink[];
};

function isRenderableChartPayload(source: { viz_data?: any; html?: string } | null | undefined): boolean {
    if (!source) return false;
    if (source.viz_data && isVizDataErrorState(source.viz_data)) return false;
    if (source.viz_data) return true;
    return typeof source.html === 'string' && source.html.trim().length > 0;
}

function isTableChartPayload(source: { viz_data?: { chart_type?: string } } | null | undefined): boolean {
    return String(source?.viz_data?.chart_type ?? '').toLowerCase() === 'table';
}

function collectHtmlChartSources(msg: AgenticChatMessage): unknown[] {
    const raw = msg as any;
    const sources: unknown[] = [];
    if (Array.isArray(raw.charts)) sources.push(...raw.charts);
    if (Array.isArray(msg.additional_kwargs?.charts)) sources.push(...msg.additional_kwargs.charts);
    return sources;
}

/**
 * `task_output.visualizations` often repeats viz_data without the ECharts `html` envelope.
 * Streaming `message.charts` (table_generation_end / viz_generation_end) has the full payload.
 * Copy html/engine/assets/render onto matching viz items so the iframe renderer can run.
 */
function enrichChartsWithHtml(out: ChartWithMeta[], sources: unknown[]): ChartWithMeta[] {
    if (out.length === 0 || sources.length === 0) return out;
    const used = new Set<number>();
    return out.map((chart) => {
        if (typeof chart.html === 'string' && chart.html.trim().length > 0) return chart;
        const chartTask = normalizeTaskIdForGrouping(chart.task_id ?? '');
        const chartIsTable = isTableChartPayload(chart);
        const idx = sources.findIndex((source, i) => {
            if (used.has(i) || !hasHtmlChartDocument(source)) return false;
            const s = source as { task_id?: string; viz_data?: { chart_type?: string } };
            const sourceTask = normalizeTaskIdForGrouping(s.task_id ?? '');
            if (chartTask && sourceTask && chartTask !== sourceTask) return false;
            return isTableChartPayload(s) === chartIsTable;
        });
        if (idx < 0) return chart;
        used.add(idx);
        return { ...chart, ...pickHtmlChartFields(sources[idx]) };
    });
}

/** Normalize a raw scenario_links payload to ScenarioLink[] (drops entries without a valid url) */
function toScenarioLinks(value: unknown): ScenarioLink[] | undefined {
    if (!Array.isArray(value)) return undefined;
    const links = value
        .filter((item): item is Record<string, unknown> => typeof item?.url === 'string')
        .map((item) => ({
            url: item.url as string,
            scenario_id: item.scenario_id as string | number | undefined,
            scenario_label: typeof item.scenario_label === 'string' ? item.scenario_label : undefined,
        }));
    return links.length > 0 ? links : undefined;
}

/**
 * When a task has multiple visualizations (e.g. 2 tables) but the backend only attaches
 * scenario_links at the task level (not per-visualization), applying the full array to every
 * visualization would duplicate every link onto every table. Distribute task-level links by
 * index instead - visualization N gets taskLinks[N] - so each table gets at most its own link.
 * Single-visualization tasks keep the legacy behavior of receiving the whole array.
 */
function scenarioLinksForVizAtIndex(
    ownLinks: ScenarioLink[] | undefined,
    taskLinks: ScenarioLink[] | undefined,
    vizIndex: number,
    vizCount: number,
): ScenarioLink[] | undefined {
    if (ownLinks) return ownLinks;
    if (!taskLinks) return undefined;
    if (vizCount <= 1) return taskLinks;
    const own = taskLinks[vizIndex];
    return own ? [own] : undefined;
}

/** Normalize assumptions/formulae to string[] for display (items in Methodology sections) */
function toItems(value: string | string[] | undefined): string[] {
    if (value == null) return [];
    return Array.isArray(value) ? value.map(String) : [String(value)];
}

/** Normalize task_id for grouping - strip _viz_N or /N suffix so all visualizations from same task group together */
function normalizeTaskIdForGrouping(taskId: string): string {
    if (!taskId) return taskId;
    return taskId.replace(/(?:_viz_?\d+|\/\d+)$/, '');
}

function getChartsFromMessage(msg: AgenticChatMessage): ChartWithMeta[] {
    const out: ChartWithMeta[] = [];
    const raw = msg as any;
    const kwargs = msg.additional_kwargs;
    const htmlSources = collectHtmlChartSources(msg);
    const streamingChartsHaveHtml = Array.isArray(raw.charts) && raw.charts.some((c: unknown) => hasHtmlChartDocument(c));

    // Prefer streaming `message.charts` when they carry ECharts html. task_complete
    // visualizations often repeat viz_data only and would otherwise hide the iframe payload.
    if (streamingChartsHaveHtml && Array.isArray(kwargs?.tasks)) {
        // Chart task_ids can carry a `_viz_N` suffix (added when multiple tables/charts share one
        // task) while kwargs.tasks keeps the plain task_id - normalize both sides before matching,
        // otherwise the task_output (and its scenario_links) lookup silently misses.
        const taskById = new Map(
            (kwargs.tasks as any[]).map((t: any) => [normalizeTaskIdForGrouping(t.task_id ?? ''), t]),
        );
        const renderableCharts = (raw.charts as any[]).filter((c) => isRenderableChartPayload(c));
        // Multiple charts (e.g. 2 tables) can share one task_id; a link array attached only at
        // the task level must be distributed one-per-table, not duplicated onto every table.
        const countByTask = new Map<string, number>();
        for (const c of renderableCharts) {
            const key = normalizeTaskIdForGrouping(c.task_id ?? '');
            countByTask.set(key, (countByTask.get(key) ?? 0) + 1);
        }
        const seenIndexByTask = new Map<string, number>();
        renderableCharts.forEach((c: any) => {
            const taskKey = normalizeTaskIdForGrouping(c.task_id ?? '');
            const t = taskById.get(taskKey);
            const result = t?.task_output?.result;
            const vizIdx = seenIndexByTask.get(taskKey) ?? 0;
            seenIndexByTask.set(taskKey, vizIdx + 1);
            const vizCount = countByTask.get(taskKey) ?? 1;
            const taskScenarioLink =
                typeof (result as any)?.scenario_link === 'string' ? (result as any).scenario_link : undefined;
            const taskScenarioLinks = toScenarioLinks((result as any)?.scenario_links);
            const ownLinks = toScenarioLinks(c.scenario_links);
            const links = scenarioLinksForVizAtIndex(ownLinks, taskScenarioLinks, vizIdx, vizCount);
            out.push({
                viz_data: c.viz_data,
                ...pickHtmlChartFields(c),
                task_id: c.task_id,
                task_name: t?.task_name ?? c.task_name,
                key_takeaways: result?.key_takeaways,
                formulae: result?.formulae,
                assumptions: result?.assumptions,
                sql_queries: t?.task_output?.sql_queries,
                table_samples: t?.task_output?.table_samples,
                scenario_link:
                    typeof c.scenario_link === 'string' ? c.scenario_link : vizCount <= 1 ? taskScenarioLink : undefined,
                scenario_links: links,
            });
        });
        if (out.length > 0) return enrichChartsWithHtml(out, htmlSources);
    }

    // 1. Primary: task_complete_events (new format) - each task can have visualizations + task_output.result
    const taskEvents = kwargs?.task_complete_events;
    if (Array.isArray(taskEvents)) {
        for (const evt of taskEvents) {
            const vizList = evt.visualizations ?? evt.task_output?.visualizations;
            if (!Array.isArray(vizList) || vizList.length === 0) continue;
            const result = evt.task_output?.result;
            const keyTakeaways = result?.key_takeaways;
            const formulae = result?.formulae;
            const assumptions = result?.assumptions;
            const taskScenarioLink =
                typeof (result as any)?.scenario_link === 'string' ? (result as any).scenario_link : undefined;
            const taskScenarioLinks = toScenarioLinks((result as any)?.scenario_links);
            const baseTaskId = evt.task_id;
            const renderableViz = vizList.filter((v: any) => isRenderableChartPayload(v));
            renderableViz.forEach((v: any, vizIdx: number) => {
                const ownLinks = toScenarioLinks((v as any).scenario_links);
                const links = scenarioLinksForVizAtIndex(ownLinks, taskScenarioLinks, vizIdx, renderableViz.length);
                out.push({
                    viz_data: v.viz_data,
                    ...pickHtmlChartFields(v),
                    task_id: v.task_id ?? baseTaskId,
                    task_name: evt.task_name ?? v.task_name,
                    key_takeaways: keyTakeaways,
                    formulae,
                    assumptions,
                    sql_queries: evt.task_output?.sql_queries,
                    table_samples: evt.task_output?.table_samples,
                    scenario_link:
                        typeof (v as any).scenario_link === 'string'
                            ? (v as any).scenario_link
                            : renderableViz.length <= 1
                              ? taskScenarioLink
                              : undefined,
                    scenario_links: links,
                });
            });
        }
        if (out.length > 0) return enrichChartsWithHtml(out, htmlSources);
    }

    // 1b. From tasks[].task_output.visualizations - one task can have multiple visualizations in same accordion
    const tasksFromKwargs = kwargs?.tasks;
    if (Array.isArray(tasksFromKwargs)) {
        for (const task of tasksFromKwargs as Array<{
            task_id?: string;
            task_name?: string;
            task_output?: { result?: any; visualizations?: Array<{ viz_data: any; task_id?: string; task_name?: string; scenario_link?: string; scenario_links?: ScenarioLink[] }>; sql_queries?: string[]; table_samples?: TableSample[] };
        }>) {
            const vizList = task.task_output?.visualizations;
            if (!Array.isArray(vizList) || vizList.length === 0) continue;
            const result = task.task_output?.result;
            const keyTakeaways = result?.key_takeaways;
            const formulae = result?.formulae;
            const assumptions = result?.assumptions;
            const taskScenarioLink = typeof result?.scenario_link === 'string' ? result.scenario_link : undefined;
            const taskScenarioLinks = toScenarioLinks(result?.scenario_links);
            const baseTaskId = task.task_id;
            const renderableViz = vizList.filter((v) => isRenderableChartPayload(v));
            renderableViz.forEach((v, vizIdx) => {
                const ownLinks = toScenarioLinks((v as any).scenario_links);
                const links = scenarioLinksForVizAtIndex(ownLinks, taskScenarioLinks, vizIdx, renderableViz.length);
                out.push({
                    viz_data: v.viz_data,
                    ...pickHtmlChartFields(v),
                    task_id: v.task_id ?? baseTaskId,
                    task_name: task.task_name ?? v.task_name,
                    key_takeaways: keyTakeaways,
                    formulae,
                    assumptions,
                    sql_queries: task.task_output?.sql_queries,
                    table_samples: task.task_output?.table_samples,
                    scenario_link:
                        typeof (v as any).scenario_link === 'string'
                            ? (v as any).scenario_link
                            : renderableViz.length <= 1
                              ? taskScenarioLink
                              : undefined,
                    scenario_links: links,
                });
            });
        }
        if (out.length > 0) return enrichChartsWithHtml(out, htmlSources);
    }

    // 2. From tasks + charts (real SSE stream) - match charts to task_output by task_id
    const tasksForCharts = kwargs?.tasks;
    if (raw.charts && Array.isArray(raw.charts) && Array.isArray(tasksForCharts)) {
        // Chart task_ids can carry a `_viz_N` suffix (added when multiple tables/charts share one
        // task) while kwargs.tasks keeps the plain task_id - normalize both sides before matching,
        // otherwise the task_output (and its scenario_links) lookup silently misses.
        const taskById = new Map(
            tasksForCharts.map((t: any) => [normalizeTaskIdForGrouping(t.task_id ?? ''), t]),
        );
        const renderableCharts = (raw.charts as any[]).filter((c) => isRenderableChartPayload(c));
        // Count how many renderable charts share each task_id so a link array attached only at
        // the task level can be distributed one-per-table instead of duplicated onto every table.
        const countByTask = new Map<string, number>();
        for (const c of renderableCharts) {
            const key = normalizeTaskIdForGrouping(c.task_id ?? '');
            countByTask.set(key, (countByTask.get(key) ?? 0) + 1);
        }
        const seenIndexByTask = new Map<string, number>();
        renderableCharts.forEach((c: any) => {
            const taskKey = normalizeTaskIdForGrouping(c.task_id ?? '');
            const t = taskById.get(taskKey);
            const result = t?.task_output?.result;
            const vizIdx = seenIndexByTask.get(taskKey) ?? 0;
            seenIndexByTask.set(taskKey, vizIdx + 1);
            const vizCount = countByTask.get(taskKey) ?? 1;
            const ownLinks = toScenarioLinks((c as any).scenario_links);
            const taskScenarioLinks = toScenarioLinks(result?.scenario_links);
            const links = scenarioLinksForVizAtIndex(ownLinks, taskScenarioLinks, vizIdx, vizCount);
            out.push({
                viz_data: c.viz_data,
                ...pickHtmlChartFields(c),
                task_id: c.task_id,
                task_name: t?.task_name ?? c.task_name,
                key_takeaways: result?.key_takeaways,
                formulae: result?.formulae,
                assumptions: result?.assumptions,
                sql_queries: t?.task_output?.sql_queries,
                table_samples: t?.task_output?.table_samples,
                scenario_link:
                    typeof (c as any).scenario_link === 'string'
                        ? (c as any).scenario_link
                        : vizCount <= 1 && typeof result?.scenario_link === 'string'
                          ? result.scenario_link
                          : undefined,
                scenario_links: links,
            });
        });
        if (out.length > 0) return enrichChartsWithHtml(out, htmlSources);
    }

    // 3. Legacy: answer.data.visualizations, charts, additional_kwargs.charts
    if (raw.answer?.data?.visualizations && Array.isArray(raw.answer.data.visualizations)) {
        raw.answer.data.visualizations.forEach((v: any) => {
            if (isRenderableChartPayload(v))
                out.push({ viz_data: v.viz_data, ...pickHtmlChartFields(v), task_id: v.task_id, task_name: v.task_name });
        });
    }
    if (raw.charts && Array.isArray(raw.charts)) {
        raw.charts.forEach((c: any) => {
            if (isRenderableChartPayload(c))
                out.push({ viz_data: c.viz_data, ...pickHtmlChartFields(c), task_id: c.task_id, task_name: c.task_name });
        });
    }
    if (kwargs?.charts && Array.isArray(kwargs.charts)) {
        kwargs.charts.forEach((c: any) => {
            if (isRenderableChartPayload(c))
                out.push({ viz_data: c.viz_data, ...pickHtmlChartFields(c), task_id: c.task_id });
        });
    }
    return enrichChartsWithHtml(out, htmlSources);
}

/**
 * A takeaway as the backend sends it: `{ text, source_tasks }` on current streams,
 * a bare string on older ones (and on Executing-step content parsed out of markdown).
 */
type RawKeyTakeaway = string | { text?: string; source_tasks?: string[] };

/** Resolves a raw `source_tasks` id to the citation badge shown after a takeaway. */
type TakeawaySourceResolver = (taskId: string) => AgenticTakeawaySource | undefined;

/** Parse key_takeaways (object or legacy string form) into TakeawayItem[] */
function parseKeyTakeaways(raw: RawKeyTakeaway[] | unknown, resolveSource?: TakeawaySourceResolver): TakeawayItem[] {
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((entry) => {
        const isObject = typeof entry === 'object' && entry !== null;
        const text = isObject ? (entry as { text?: string }).text : entry;
        if (text == null || String(text).trim() === '') return [];
        const item: TakeawayItem = {
            segments: [{ text: String(text), isBold: false }] as TakeawayTextSegment[],
        };
        const rawSources = isObject ? (entry as { source_tasks?: string[] }).source_tasks : undefined;
        if (resolveSource && Array.isArray(rawSources)) {
            const sources = rawSources
                .map((taskId) => (typeof taskId === 'string' ? resolveSource(taskId) : undefined))
                .filter((source): source is AgenticTakeawaySource => source != null);
            if (sources.length > 0) item.sourceTasks = sources;
        }
        return [item];
    });
}

/** Extract query, overview, key_takeaways, key_findings, formulae, assumptions from Executing step content (may contain multiple ✅ chunks) */
function parseExecutingContent(content: string): Array<{
    query?: string;
    overview?: string;
    key_takeaways?: string[];
    key_findings?: string[];
    formulae?: string | string[];
    assumptions?: string | string[];
}> {
    const results: Array<{
        query?: string;
        overview?: string;
        key_takeaways?: string[];
        key_findings?: string[];
        formulae?: string | string[];
        assumptions?: string | string[];
    }> = [];
    if (typeof content !== 'string') return results;

    // Split by ✅ to get individual task outputs
    const chunks = content
        .split(/✅\s*/)
        .map((s) => s.trim())
        .filter(Boolean);

    for (const chunk of chunks) {
        let parsed: Record<string, unknown> | null = null;
        // Try parsing: first as-is (already valid JSON), then with Python-style key conversion
        try {
            parsed = JSON.parse(chunk) as Record<string, unknown>;
        } catch {
            try {
                // Convert Python-style dict keys ('key':) to JSON ("key":) without touching string values
                // (values may contain apostrophes e.g. "NUTRIFUEL's" - naive replace all ' breaks those)
                const jsonStr = chunk.replace(/'([^']*)'\s*:/g, '"$1":');
                parsed = JSON.parse(jsonStr) as Record<string, unknown>;
            } catch {
                // Fallback: extract overview via regex when JSON parse fails
                // Allow quoted keys: 'overview', "overview", or bare overview
                const overviewMatch =
                    chunk.match(/(?:'overview'|"overview"|overview)\s*:\s*"((?:[^"\\]|\\.)*)"/) ??
                    chunk.match(/(?:'overview'|"overview"|overview)\s*:\s*'((?:[^'\\]|\\.)*)'/);
                if (overviewMatch) {
                    results.push({ overview: overviewMatch[1] });
                }
            }
        }
        if (parsed && typeof parsed === 'object') {
            const formulae = Array.isArray(parsed.formulae)
                ? parsed.formulae.map(String)
                : typeof parsed.formulae === 'string'
                  ? parsed.formulae
                  : undefined;
            const assumptions = Array.isArray(parsed.assumptions)
                ? parsed.assumptions.map(String)
                : typeof parsed.assumptions === 'string'
                  ? parsed.assumptions
                  : undefined;
            results.push({
                query: typeof parsed.query === 'string' ? parsed.query : undefined,
                overview: typeof parsed.overview === 'string' ? parsed.overview : undefined,
                key_takeaways: Array.isArray(parsed.key_takeaways) ? parsed.key_takeaways.map(String) : undefined,
                key_findings: Array.isArray(parsed.key_findings) ? parsed.key_findings.map(String) : undefined,
                formulae,
                assumptions,
            });
        }
    }
    return results;
}

type TaskLikeForPhase = {
    content?: string;
    task_output?: {
        result?: { query?: string; overview?: string };
        visualizations?: unknown[];
    };
    status?: string;
};

/** True once a task has started or finished (task_start content, task_output, or explicit status) — not a planning-only stub. */
function taskHasExecutionSignal(task: TaskLikeForPhase): boolean {
    const result = task.task_output?.result;
    if (typeof result?.query === 'string' && result.query.trim() !== '') return true;
    if (typeof result?.overview === 'string' && result.overview.trim() !== '') return true;
    if (typeof task.content === 'string' && task.content.trim() !== '') return true;
    const viz = task.task_output?.visualizations;
    if (Array.isArray(viz) && viz.length > 0) return true;
    const st = (task.status ?? '').toLowerCase();
    if (['running', 'in_progress', 'completed', 'done', 'executing', 'started'].some((s) => st.includes(s))) return true;
    return false;
}

function parseStringList(value: unknown): string[] {
    if (!Array.isArray(value)) return [];
    return value.map(String).map((s) => s.trim()).filter(Boolean);
}

/** Supported analysis types for out-of-scope feedback (kwargs, step content, or message body). */
function parseSupportedAnalysesFromOutOfScope(
    msg: AgenticChatMessage,
    outOfScopeStep: AgenticIntermediateStep | undefined,
    contentText: string,
): string[] {
    const kwargs = msg.additional_kwargs;
    const fromKwargs = parseStringList(kwargs?.supported_analyses ?? kwargs?.capabilities);
    if (fromKwargs.length > 0) return fromKwargs;

    const stepContent = outOfScopeStep?.content;
    if (Array.isArray(stepContent)) {
        const fromStep = parseStringList(stepContent);
        if (fromStep.length > 0) return fromStep;
    }
    if (typeof stepContent === 'string' && stepContent.trim() !== '') {
        try {
            const parsed = JSON.parse(stepContent) as unknown;
            if (Array.isArray(parsed)) return parseStringList(parsed);
            if (parsed && typeof parsed === 'object') {
                const obj = parsed as Record<string, unknown>;
                const nested = parseStringList(obj.supported_analyses ?? obj.capabilities);
                if (nested.length > 0) return nested;
            }
        } catch {
            // fall through to text parsing
        }
    }

    const marker = /WHAT I CAN HELP WITH TODAY/i;
    const markerMatch = contentText.match(marker);
    if (markerMatch?.index != null) {
        const afterMarker = contentText.slice(markerMatch.index + markerMatch[0].length);
        const items: string[] = [];
        for (const rawLine of afterMarker.split('\n')) {
            const line = rawLine.trim();
            if (!line) {
                if (items.length > 0) break;
                continue;
            }
            const bullet = line.replace(/^[-•*]\s*/, '').trim();
            if (!bullet || /^what i can help with today$/i.test(bullet)) continue;
            if (!/^[-•*]/.test(line) && items.length > 0) break;
            if (bullet) items.push(bullet);
        }
        if (items.length > 0) return items;
    }

    return [];
}

/**
 * Get key takeaways for the Analysis Summary from global (message-level) sources only.
 * Does not merge from individual task key_takeaways - avoids content flip when
 * global key_takeaways arrives in the final stream.
 */
function getKeyTakeawaysFromMessage(
    msg: AgenticChatMessage,
    resolveSource?: TakeawaySourceResolver,
): TakeawayItem[] {
    const raw = msg as any;
    const fromMsg =
        msg.additional_kwargs?.key_takeaways ?? msg.key_takeaways ?? raw.answer?.data?.key_takeaways;
    if (Array.isArray(fromMsg) && fromMsg.length > 0) {
        return parseKeyTakeaways(fromMsg, resolveSource);
    }
    return [];
}

type ExecutingChunk = {
    /** Query text for this task - shown in phase card middle strip */
    query?: string;
    overview?: string;
    key_findings?: string[];
    key_takeaways?: string[];
    formulae?: string | string[];
    assumptions?: string | string[];
};

function executingChunkHasDisplayContent(chunk: ExecutingChunk): boolean {
    if (typeof chunk.query === 'string' && chunk.query.trim() !== '') return true;
    if (typeof chunk.overview === 'string' && chunk.overview.trim() !== '') return true;
    if (Array.isArray(chunk.key_takeaways) && chunk.key_takeaways.length > 0) return true;
    if (Array.isArray(chunk.key_findings) && chunk.key_findings.length > 0) return true;
    return false;
}

function getFollowupQuestions(msg: AgenticChatMessage): SuggestedAction[] {
    const raw = (msg as any).answer?.data?.followup_question ?? msg.additional_kwargs?.followup_questions;
    if (!raw) return [];

    const parseOne = (q: any, i: number): SuggestedAction | null => {
        if (typeof q === 'string') {
            return { id: `f-${i}`, label: q, question: q };
        }
        const keyword = q?.keyword ?? q?.label ?? '';
        const question = q?.question ?? q?.text ?? (typeof q === 'string' ? q : String(q ?? ''));
        if (!keyword && !question) return null;
        return {
            id: `f-${i}`,
            label: keyword || question,
            question: question || keyword,
        };
    };

    if (Array.isArray(raw)) {
        return raw.map((q, i) => parseOne(q, i)).filter((a): a is SuggestedAction => a != null);
    }
    if (typeof raw === 'string') {
        try {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr)) {
                return arr.map((q: any, i: number) => parseOne(q, i)).filter((a): a is SuggestedAction => a != null);
            }
        } catch {
            // ignore
        }
    }
    return [];
}

function normalizeChartType(chartType: string): 'bar' | 'line' | 'area' | 'bpl' | 'table' | 'waterfall' | 'stackbar' | 'horizontalbar' {
    const t = String(chartType || '').toLowerCase();
    // PIC (Price Incentive Curve) renders as a line chart
    if (t === 'pic') return 'line';
    if (t.includes('waterfall')) return 'waterfall';
    if (t.includes('stack') && t.includes('bar')) return 'stackbar';
    if (t.includes('horizontal') && t.includes('bar')) return 'horizontalbar';
    if (t.includes('bar')) return 'bar';
    if (t.includes('line')) return 'line';
    if (t.includes('area')) return 'area';
    if (t.includes('bpl') || (t.includes('brand') && t.includes('price'))) return 'bpl';
    if (t.includes('table')) return 'table';
    return 'bar';
}

export interface DerivedLayoutState {
    conversationPairs: AgenticConversationPair[];
    runningTasks: AgenticRunningTask[];
    /** Ordered blocks: summary then artifacts per message (Q1 summary+artifacts, Q2 summary+artifacts, ...) */
    rightPanelBlocks: RightPanelBlock[];
    /** Flattened accordion items from all right panel blocks */
    accordionItems: AgenticAccordionItem[];
    /** First non-undefined summary from right panel blocks */
    summary: AgenticAnalysisSummary | undefined;
    leftPanelTimestamp: string | undefined;
}

/**
 * Derives layout state from messages and optional loading message.
 * Each message = one question-response pair. Per-pair: starting text (Refining/Understanding/History Use),
 * planner tasks (Planning), phase cards (Executing), followup questions.
 */
export function deriveLayoutFromMessages(
    messages: AgenticChatMessage[],
    loadingMessage: AgenticChatMessage | null | undefined,
    feedbackHandlers?: DeriveLayoutFeedbackHandlers,
    feedbackHistory?: AgenticFeedbackHistoryResponse,
): DerivedLayoutState {
    const allMessages = loadingMessage ? [...messages, loadingMessage] : [...messages];
    const isLoading = !!loadingMessage;

    const conversationPairs: AgenticConversationPair[] = [];
    const rightPanelBlocks: RightPanelBlock[] = [];
    const runningTasks: AgenticRunningTask[] = [];

    for (let msgIdx = 0; msgIdx < allMessages.length; msgIdx++) {
        const msg = allMessages[msgIdx];
        const isLastAndLoading = isLoading && msg === loadingMessage;
        const steps = getIntermediateSteps(msg);
        const idBase = msg.id ?? msgIdx;

        // 1. Question from user_input
        const question = msg.user_input ?? (msg as any).question ?? '';
        const userMessage: AgenticConversationMessage = {
            id: `user-${idBase}`,
            type: 'user',
            content: question,
            timestamp: msg.created_on,
        };

        // 2. Starting response text from Refining, Understanding, or History Use step
        // Out of Scope / Interrupt: show full content in left panel, not in Summary card
        // Interrupt cases: (a) explicit interrupt flag in additional_kwargs, (b) Understanding completed without Executing
        const outOfScopeStep = steps.find((s) => s.step === 'Out of Scope' && s.status === 'completed');
        const understandingStep = steps.find((s) => s.step === 'Understanding' && s.status === 'completed');
        const hasUnderstanding = steps.some((s) => s.step === 'Understanding');
        const hasExecutingCompleted = steps.some((s) => s.step === 'Executing' && s.status === 'completed');
        const explicitInterrupt = msg.additional_kwargs?.interrupt === true;
        const isOutOfScope = outOfScopeStep != null;
        const isOutOfScopeOrInterrupt =
            isOutOfScope || explicitInterrupt || (hasUnderstanding && !hasExecutingCompleted);

        const contentTextForMessage = getContentFromMessage(msg);
        const refiningStep = steps.find((s) => s.step === 'Refining' && s.status === 'completed');
        const historyUseStep = steps.find((s) => s.step === 'History Use' && s.status === 'completed');
        const startingStep = refiningStep ?? understandingStep ?? historyUseStep;
        const startingTextFromStep =
            startingStep && typeof startingStep.content === 'string' ? startingStep.content : '';
        /** History replay often only has History Use with a short status line; use stored answer text like a completed narrative bubble. */
        const useAnswerBodyForHistoryOnlyStarting =
            historyUseStep != null &&
            refiningStep == null &&
            understandingStep == null &&
            Boolean(contentTextForMessage);
        const startingText = useAnswerBodyForHistoryOnlyStarting
            ? contentTextForMessage
            : startingTextFromStep
              ? startingTextFromStep
              : isLastAndLoading
                ? 'Generating...'
                : '';

        // For Out of Scope or Interrupt: use full response content in left panel (not short step text)
        const assistantContent: React.ReactNode | string =
            isLastAndLoading && !startingText
                ? (() => {
                      const execStep = steps.find((s) => s.status === 'executing');
                      return execStep
                          ? typeof execStep.content === 'string'
                              ? execStep.content
                              : 'Generating...'
                          : 'Generating...';
                  })()
                : isOutOfScopeOrInterrupt && contentTextForMessage
                  ? contentTextForMessage
                  : startingText;

        const assistantMessage: AgenticConversationMessage = {
            id: `assistant-${idBase}`,
            type: 'assistant',
            content: assistantContent,
            timestamp: msg.created_on,
        };

        // 3. Running tasks (only when loading)
        if (isLastAndLoading && loadingMessage) {
            const executingStep = steps.find((s) => s.status === 'executing');
            if (executingStep) {
                const text =
                    typeof executingStep.content === 'string'
                        ? `${executingStep.step}: ${executingStep.content}`
                        : `${executingStep.step}...`;
                runningTasks.push({ id: `running-${msgIdx}`, text });
            }
        }

        // 4. Planner tasks (grey box) from Planning step - format "task_name: description"
        let plannerTasks: AgenticPlannerTasks | undefined;
        const planningStep = steps.find((s) => s.step === 'Planning' && s.status === 'completed');
        if (planningStep?.content && Array.isArray(planningStep.content)) {
            const tasks = (planningStep.content as AgenticPlanningTask[]).map((t, i) => {
                const taskName = t.task_name ?? 'Task';
                const desc = t.description ?? '';
                const text = desc ? `**${taskName}**: ${desc}` : `**${taskName}**`;
                return { id: t.task_id ?? `task-${i}`, text };
            });
            if (tasks.length > 0) {
                plannerTasks = { id: `planner-${idBase}`, tasks };
            }
        }

        // 5. Phase cards - show incrementally from task_start events (not all after planning)
        // Description: first show 'content' from task_start; when task_complete fires, show 'query' from task_output.result
        const phaseCards: AgenticPhaseCard[] = [];
        const planningTasks =
            planningStep?.content && Array.isArray(planningStep.content)
                ? (planningStep.content as AgenticPlanningTask[])
                : [];
        const executingStep = steps.find((s) => s.step === 'Executing' && s.status === 'completed');
        // Prefer task_complete_events or tasks for chunks (overview, formulae, key_takeaways) when available
        let executingChunks: ExecutingChunk[];
        const taskEvents = msg.additional_kwargs?.task_complete_events;
        const tasksForChunks = msg.additional_kwargs?.tasks;
        const taskSource =
            Array.isArray(taskEvents) && taskEvents.length > 0
                ? taskEvents
                : Array.isArray(tasksForChunks)
                  ? tasksForChunks
                  : [];
        if (taskSource.length > 0) {
            executingChunks = taskSource.map((evt: any) => {
                const result = evt.task_output?.result;
                // query populates phase card middle strip - from result.query or event top-level
                const query = result?.query ?? evt.query ?? evt.task_output?.query;
                return {
                    query: typeof query === 'string' ? query : undefined,
                    overview: result?.overview,
                    key_takeaways: result?.key_takeaways,
                    key_findings: result?.key_findings,
                    formulae: result?.formulae,
                    assumptions: result?.assumptions,
                };
            });
        } else {
            executingChunks =
                executingStep?.content && typeof executingStep.content === 'string'
                    ? parseExecutingContent(executingStep.content)
                    : [];
        }

        // Build set of task_ids that have generated artifacts - View Artifact only shown when artifact exists and is linked
        // Keep ALL visualizations (chart + table for same task) - group by task_id for accordion cards
        const chartsRaw = getChartsFromMessage(msg);
        const taskIdsWithArtifacts = new Set(
            chartsRaw.map((c) => c.task_id).filter((id): id is string => id != null && id !== ''),
        );
        // Normalized for citation lookups - chart task_ids can carry a `_viz_N` suffix
        const normalizedTaskIdsWithArtifacts = new Set(
            Array.from(taskIdsWithArtifacts, (id) => normalizeTaskIdForGrouping(id) || id),
        );

        // Task serial per normalized task_id, shared by the summary's citation badges, the
        // artefact card headers and the left panel task labels so all three agree.
        const taskSerialByTaskId = new Map<string, number>();
        const registerTaskSerial = (taskId: string | undefined, serial: number) => {
            if (!taskId) return;
            taskSerialByTaskId.set(normalizeTaskIdForGrouping(taskId) || taskId, serial);
        };
        // Cited task with no artefact card still gets a badge (numbering stays truthful),
        // just without a click target. Populated by the phase-card loops below.
        const resolveTakeawaySource = (rawTaskId: string): AgenticTakeawaySource | undefined => {
            const taskId = normalizeTaskIdForGrouping(rawTaskId) || rawTaskId;
            const serial = taskSerialByTaskId.get(taskId);
            if (serial == null) return undefined;
            return { taskId, serial, hasArtefact: normalizedTaskIdsWithArtifacts.has(taskId) };
        };

        // Primary: use tasks from task_start/task_complete for incremental phase cards (omit planning-only stubs)
        const tasks = msg.additional_kwargs?.tasks;
        if (Array.isArray(tasks) && tasks.length > 0) {
            let phaseIndex = 0;
            for (let i = 0; i < tasks.length; i++) {
                const task = tasks[i] as TaskLikeForPhase & { task_id?: string; task_name?: string };
                if (!taskHasExecutionSignal(task)) continue;
                phaseIndex += 1;
                const taskName = task.task_name ?? `Task ${phaseIndex}`;
                const taskId = task.task_id ?? `phase-${idBase}-${i}`;
                const result = task.task_output?.result;
                // Description: task_complete → result.query; task_start → content
                const queryFromComplete = typeof result?.query === 'string' ? result.query : undefined;
                const description =
                    queryFromComplete ??
                    (typeof task.content === 'string' ? task.content : undefined) ??
                    'Executing tasks ...';
                const viewArtifactTaskId =
                    task.task_id && taskIdsWithArtifacts.has(task.task_id) ? task.task_id : undefined;
                registerTaskSerial(task.task_id ?? taskId, phaseIndex);
                phaseCards.push({
                    id: taskId,
                    phaseNumber: phaseIndex,
                    title: taskName,
                    description,
                    overview: result?.overview ?? executingChunks[i]?.overview,
                    taskId: viewArtifactTaskId,
                    isCollapsed: false,
                });
            }
        } else {
            // Fallback: only rows backed by executing output (do not invent cards from planning alone)
            let phaseIndex = 0;
            for (let i = 0; i < executingChunks.length; i++) {
                const planTask = planningTasks[i];
                const execChunk = executingChunks[i];
                if (!executingChunkHasDisplayContent(execChunk)) continue;
                phaseIndex += 1;
                const taskName = planTask?.task_name ?? `Task ${phaseIndex}`;
                const taskId = planTask?.task_id ?? `phase-${idBase}-${i}`;
                const viewArtifactTaskId =
                    planTask?.task_id && taskIdsWithArtifacts.has(planTask.task_id) ? planTask.task_id : undefined;
                registerTaskSerial(planTask?.task_id ?? taskId, phaseIndex);
                const description =
                    (typeof execChunk.query === 'string' && execChunk.query.trim() !== ''
                        ? execChunk.query
                        : undefined) ??
                    (typeof execChunk.overview === 'string' ? execChunk.overview : undefined) ??
                    'Executing tasks ...';
                phaseCards.push({
                    id: taskId,
                    phaseNumber: phaseIndex,
                    title: taskName,
                    description,
                    overview: execChunk?.overview,
                    taskId: viewArtifactTaskId,
                    isCollapsed: false,
                });
            }
        }

        // 6. Followup questions
        const suggestedActions = getFollowupQuestions(msg);

        // 6b. Summary text for left panel (overview preferred over content, shown above followups)
        // Out of Scope / interrupt: full reply is already in assistantMessage (contentTextForMessage).
        // Do not fall back to msg.content / contentTextForMessage here or the left panel shows the same text twice.
        const summaryText = useAnswerBodyForHistoryOnlyStarting
            ? (msg.additional_kwargs?.overview ?? msg.overview) || undefined
            : isOutOfScopeOrInterrupt
              ? (msg.additional_kwargs?.overview ?? msg.overview) || undefined
              : (msg.additional_kwargs?.overview ??
                    msg.overview ??
                    msg.content ??
                    contentTextForMessage) || undefined;

        const entityDisambiguation = getEntityDisambiguationFromMessage(
            msg,
            msgIdx === allMessages.length - 1,
        );

        const questionLabel = getRightPanelQuestionLabel(msg, question, msgIdx);
        const runId = msg.run_id;
        const sessionId = msg.thread_id;

        const outOfScopeFeedback: AgenticOutOfScopeFeedback | undefined = isOutOfScope
            ? {
                  supportedAnalyses: parseSupportedAnalysesFromOutOfScope(msg, outOfScopeStep, contentTextForMessage),
                  ...(() => {
                      // Query-level feedback (feedback_level: "query", run_id only) — same as Summary artefact.
                      const handlers = buildAccordionFeedbackHandlers(
                          feedbackHandlers,
                          {
                              kind: 'summary',
                              messageIndex: msgIdx,
                              questionTitle: questionLabel,
                              accordionItemId: `msg-out-of-scope-${idBase}`,
                              run_id: runId,
                              session_id: sessionId,
                          },
                          questionLabel,
                      );
                      return {
                          onDislikeFeedbackSubmit: handlers.onDislikeFeedbackSubmit,
                          dislikeFeedbackContextTitle: handlers.dislikeFeedbackContextTitle,
                      };
                  })(),
              }
            : undefined;

        conversationPairs.push({
            userMessage,
            assistantMessage,
            plannerTasks,
            phaseCards,
            suggestedActions,
            summaryText: summaryText || undefined,
            disableTypewriter: !!msg.receivedAiEvent,
            entityDisambiguation,
            outOfScopeFeedback,
        });

        // 7. Right panel block - Summary card nested inside main question accordion
        const keyTakeaways = getKeyTakeawaysFromMessage(msg, resolveTakeawaySource); // Primary data source for summary card

        const blockAccordionItems: AgenticAccordionItem[] = [];

        // Analysis Summary as child accordion item - shown when key_takeaways is present
        const summaryChildItem: AgenticChildAccordionItem | undefined =
            keyTakeaways.length > 0 && !isOutOfScopeOrInterrupt
                ? {
                      id: `msg-summary-${idBase}`,
                      run_id: runId,
                      session_id: sessionId,
                      title: 'Summary',
                      defaultExpanded: true,
                      content: {
                          type: 'analysisSummary',
                          keyTakeaways: { title: 'Key Findings', items: keyTakeaways },
                      } as AnalysisSummaryContent,
                      ...buildAccordionFeedbackHandlers(
                          feedbackHandlers,
                          {
                              kind: 'summary',
                              messageIndex: msgIdx,
                              questionTitle: questionLabel,
                              accordionItemId: `msg-summary-${idBase}`,
                              run_id: runId,
                              session_id: sessionId,
                          },
                          'Summary',
                          getInitialVoteForSummary(feedbackHistory, runId),
                      ),
                  }
                : undefined;

        // Group all visualizations by task_id - one accordion per task, all visualizations inside
        const chartsByTask = new Map<string, ChartWithMeta[]>();
        for (const chart of chartsRaw) {
            const rawTaskId = chart.task_id ?? `chart-${idBase}`;
            const taskId = normalizeTaskIdForGrouping(rawTaskId) || rawTaskId;
            const list = chartsByTask.get(taskId) ?? [];
            list.push(chart);
            chartsByTask.set(taskId, list);
        }
        // Chart insertion order follows `chartsRaw` (streaming/event arrival order), which can
        // differ from the task execution order shown in the left panel phase cards (e.g. a later
        // task's events land first). Sort by the same taskSerial used for phase cards so both
        // panels agree on task order.
        const orderedChartTaskEntries = Array.from(chartsByTask.entries()).sort(
            ([taskIdA], [taskIdB]) =>
                (taskSerialByTaskId.get(taskIdA) ?? Number.MAX_SAFE_INTEGER) -
                (taskSerialByTaskId.get(taskIdB) ?? Number.MAX_SAFE_INTEGER),
        );
        const chartChildren: AgenticChildAccordionItem[] = orderedChartTaskEntries.map(
            ([taskId, taskCharts]) => {
                const firstChart = taskCharts[0];
                const taskTitle =
                    firstChart.task_name ?? firstChart.viz_data?.chartTitle ?? `Visualization ${taskId}`;
                const formulae =
                    firstChart.formulae ??
                    executingChunks[0]?.formulae;
                const assumptions = firstChart.assumptions ?? executingChunks[0]?.assumptions;
                const chartKeyTakeaways =
                    firstChart.key_takeaways && firstChart.key_takeaways.length > 0
                        ? parseKeyTakeaways(firstChart.key_takeaways)
                        : keyTakeaways;
                const assumptionsItems = toItems(assumptions);
                const formulaeItems = toItems(formulae);
                const showFormulaeInBox = formulaeItems.length > 0;
                const chartMethodologySections = [
                    ...(assumptionsItems.length > 0 ? [{ title: 'Assumptions', items: assumptionsItems }] : []),
                    ...(!showFormulaeInBox && formulaeItems.length > 0
                        ? [{ title: 'Formulae', items: formulaeItems }]
                        : []),
                ];
                const formulaForBox = showFormulaeInBox
                    ? formulaeItems.length === 1
                        ? formulaeItems[0]
                        : formulaeItems
                    : undefined;
                const sqlQueries = firstChart.sql_queries ?? undefined;
                const tableSamples = firstChart.table_samples;
                const visualizations = taskCharts.map((c) => ({
                    chartType: normalizeChartType(c.viz_data?.chart_type),
                    vizData: c.viz_data,
                    ...pickHtmlChartFields(c),
                    scenarioLink: typeof c.scenario_link === 'string' ? c.scenario_link : undefined,
                    scenarioLinks: c.scenario_links,
                }));
                const content: ChartVisualizationContent = {
                    type: 'chartVisualization',
                    keyTakeaways:
                        chartKeyTakeaways.length > 0
                            ? { title: 'Key Takeaways', items: chartKeyTakeaways }
                            : undefined,
                    visualizations,
                    methodology:
                        chartMethodologySections.length > 0 || formulaForBox || sqlQueries || tableSamples?.length
                            ? {
                                  title: 'Methodology',
                                  sections: chartMethodologySections,
                                  formula: formulaForBox,
                                  sqlQuery: sqlQueries,
                                  tableSamples,
                              }
                            : undefined,
                };
                return {
                    id: taskId,
                    run_id: runId,
                    session_id: sessionId,
                    title: taskTitle,
                    taskSerial: taskSerialByTaskId.get(taskId),
                    defaultExpanded: true,
                    content,
                    ...buildAccordionFeedbackHandlers(
                        feedbackHandlers,
                        {
                            kind: 'task',
                            messageIndex: msgIdx,
                            questionTitle: questionLabel,
                            taskId,
                            taskTitle,
                            accordionItemId: taskId,
                            run_id: runId,
                            session_id: sessionId,
                        },
                        taskTitle,
                        getInitialVoteForTask(feedbackHistory, runId, taskId),
                    ),
                };
            },
        );

        // When we have summary and/or charts: wrap in main question accordion with summary first
        if (summaryChildItem != null || chartChildren.length > 0) {
            const allChildren: AgenticChildAccordionItem[] = [
                ...(summaryChildItem != null ? [summaryChildItem] : []),
                ...chartChildren,
            ];
            /** When true, right panel shows Analysis Summary loader until key_takeaways arrive — needs parent accordion so loader sits under the question header, above artifact cards. */
            const analysisSummaryPending = isLastAndLoading && summaryChildItem == null;
            if (allChildren.length === 1 && chartChildren.length === 1 && !analysisSummaryPending) {
                // Single chart only, no summary - child already includes feedback from chartChildren map
                const onlyChart = chartChildren[0];
                blockAccordionItems.push({
                    ...onlyChart,
                    defaultExpanded: true,
                });
            } else if (allChildren.length === 1 && summaryChildItem != null) {
                // Summary only - wrap in parent accordion
                blockAccordionItems.push({
                    id: `group-${idBase}`,
                    title: questionLabel,
                    index: msgIdx + 1,
                    defaultExpanded: true,
                    children: [summaryChildItem],
                });
            } else {
                // Summary + charts, or multiple charts - parent accordion with all children
                blockAccordionItems.push({
                    id: `group-${idBase}`,
                    title: questionLabel,
                    index: msgIdx + 1,
                    defaultExpanded: true,
                    children: allChildren,
                });
            }
        }

        // No separate blockSummary - content is always in the Analysis Summary accordion (with like/dislike, collapse)
        // Add block when we have content, OR when loading (show loading state for analysis summary)
        if (blockAccordionItems.length > 0) {
            rightPanelBlocks.push({
                messageIndex: msgIdx,
                userQuery: questionLabel,
                runId,
                summary: undefined,
                accordionItems: blockAccordionItems,
                // Keep summary loader visible until summary (key_takeaways) arrives, even when artifacts have started
                summaryLoading: isLastAndLoading && summaryChildItem == null ? true : undefined,
            });
        } else if (isLastAndLoading) {
            // Show loading state for analysis summary until content is ready
            rightPanelBlocks.push({
                messageIndex: msgIdx,
                userQuery: questionLabel,
                runId,
                summary: undefined,
                summaryLoading: true,
                accordionItems: [],
            });
        }
    }

    const lastMsg = allMessages[allMessages.length - 1];
    const leftPanelTimestamp = lastMsg?.created_on;

    // Flatten accordion items and extract first summary from all blocks
    const accordionItems: AgenticAccordionItem[] = rightPanelBlocks.flatMap((b) => b.accordionItems);
    const summary = rightPanelBlocks.find((b) => b.summary != null)?.summary;

    return {
        conversationPairs,
        runningTasks,
        rightPanelBlocks,
        accordionItems,
        summary,
        leftPanelTimestamp,
    };
}
